import { ReactNode } from 'react';

import {
  Button,
  Icon,
  IconButton,
  ListTable,
  RadioGroup,
  Switch,
} from '@atom/mui';
import { AddIcon, DeleteIcon, EditIcon } from '@atom/mui/icons';
import colors from '@atom/styles/colors';
import { ConversionUnit } from '@atom/types/schema';
import { setDisplayDate } from '@atom/utilities/timeUtilities';
import { getUserFullName } from '@atom/utilities/userUtilities';

import classes from './conversionUnitsTable.module.css';

const { TableHead, TableRow, TableCell, TableBody } = ListTable;
const { RadioButton } = RadioGroup;

const styles = {
  headCell: {
    padding: '0 1rem',
    boxSizing: 'border-box' as const,
    whiteSpace: 'nowrap' as const,
    overflow: 'hidden',
  },
  bodyCell: {
    padding: '0 1rem',
    boxSizing: 'border-box' as const,
    overflowWrap: 'anywhere' as const,
    color: colors.neutral.dark,
  },
  addCell: { padding: '0 0.5rem' },
  equalsCell: { padding: 0 },
  iconButton: { padding: 0 },
};

const CONVERSION_UNIT_COLUMN_WIDTH = '8.5rem';
export const EQUALS_COLUMN_WIDTH = '2.5rem';
const TOGGLE_COLUMN_WIDTH = '5.75rem';
const ACTIONS_COLUMN_WIDTH = '6.5rem';

type ConversionUnitsTableProps = {
  conversionUnits: ConversionUnit[];
  /**
   * the schema's unit that each conversion unit converts to
   */
  baseUnit: string;
  /**
   * shows the Added By and Added On columns
   */
  showAddedColumns?: boolean;
  /**
   * the default radio and active switch are disabled when their handler is omitted
   */
  onDefaultChange?: (id: string) => void;
  onActiveChange?: (id: string, active: boolean) => void;
  /**
   * the edit and delete buttons are hidden when their handler is omitted
   */
  onEdit?: (conversionUnit: ConversionUnit) => void;
  onDelete?: (id: string) => void;
  /**
   * hides the edit and delete buttons on rows it returns false for
   */
  canModify?: (conversionUnit: ConversionUnit) => boolean;
  /**
   * shows the Add Conversion Unit row at the top of the table body
   */
  onAdd?: () => void;
  /**
   * disables the Add Conversion Unit button, e.g. until a base unit is chosen
   */
  addDisabled?: boolean;
  /**
   * replaces the Add Conversion Unit button while the add/edit form is open.
   * Returns table cells that fill columnCount columns.
   */
  renderForm?: (columnCount: number) => ReactNode;
  dataCy?: string;
};

const ConversionUnitsTable = ({
  conversionUnits,
  baseUnit,
  showAddedColumns = false,
  onDefaultChange,
  onActiveChange,
  onEdit,
  onDelete,
  canModify = () => true,
  onAdd,
  addDisabled = false,
  renderForm,
  dataCy = 'conversionUnitsTable',
}: ConversionUnitsTableProps) => {
  const showActions = !!onEdit || !!onDelete;
  const showAddRow = !!onAdd || !!renderForm;
  const addIconColor = addDisabled ? colors.neutral.silver : colors.brand.blue;
  const columnCount = 5 + (showAddedColumns ? 2 : 0) + (showActions ? 1 : 0);

  return (
    <div className={classes.tableContainer}>
      <ListTable fullHeight={false} className={classes.table}>
        <TableHead>
          <TableRow header height="2.5rem">
            <TableCell
              variant="head"
              width={CONVERSION_UNIT_COLUMN_WIDTH}
              style={styles.headCell}
            >
              Conversion Unit
            </TableCell>
            <TableCell
              variant="head"
              width={EQUALS_COLUMN_WIDTH}
              style={styles.equalsCell}
            />
            <TableCell variant="head" style={styles.headCell}>
              Conversion
            </TableCell>
            <TableCell
              variant="head"
              center
              width={TOGGLE_COLUMN_WIDTH}
              style={styles.headCell}
            >
              Default
            </TableCell>
            <TableCell
              variant="head"
              center
              width={TOGGLE_COLUMN_WIDTH}
              style={styles.headCell}
            >
              Active
            </TableCell>
            {showAddedColumns && (
              <TableCell variant="head" style={styles.headCell}>
                Added By
              </TableCell>
            )}
            {showAddedColumns && (
              <TableCell variant="head" style={styles.headCell}>
                Added On
              </TableCell>
            )}
            {showActions && (
              <TableCell
                variant="head"
                width={ACTIONS_COLUMN_WIDTH}
                style={styles.headCell}
              />
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {showAddRow && (
            <TableRow hover={false} height="3.25rem">
              {renderForm ? (
                renderForm(columnCount)
              ) : (
                <TableCell colSpan={columnCount} style={styles.addCell}>
                  <Button
                    color="primary"
                    startIcon={<Icon icon={AddIcon} color={addIconColor} />}
                    disabled={addDisabled}
                    onClick={onAdd}
                    data-cy={`${dataCy}AddButton`}
                  >
                    Add Conversion Unit
                  </Button>
                </TableCell>
              )}
            </TableRow>
          )}
          {!showAddRow && conversionUnits.length === 0 ? (
            <TableRow hover={false}>
              <TableCell colSpan={columnCount} style={styles.bodyCell}>
                <span className={classes.emptyText}>No conversion units</span>
              </TableCell>
            </TableRow>
          ) : (
            conversionUnits.map(conversionUnit => (
              <TableRow
                key={conversionUnit.id}
                height="3.125rem"
                data-cy={`${dataCy}Row-${conversionUnit.unit}`}
              >
                <TableCell style={styles.bodyCell}>
                  1 <span className={classes.unit}>{conversionUnit.unit}</span>
                </TableCell>
                <TableCell
                  center
                  width={EQUALS_COLUMN_WIDTH}
                  style={{ ...styles.bodyCell, ...styles.equalsCell }}
                >
                  =
                </TableCell>
                <TableCell style={styles.bodyCell}>
                  {conversionUnit.conversionNumber}{' '}
                  <span className={classes.unit}>{baseUnit}</span>
                </TableCell>
                <TableCell
                  center
                  width={TOGGLE_COLUMN_WIDTH}
                  style={styles.bodyCell}
                >
                  <RadioButton
                    value={conversionUnit.id}
                    label=""
                    checked={conversionUnit.isDefault}
                    disabled={!onDefaultChange}
                    onChange={() => onDefaultChange?.(conversionUnit.id)}
                    sx={{ margin: 0 }}
                    data-cy={`${dataCy}DefaultRadio-${conversionUnit.unit}`}
                  />
                </TableCell>
                <TableCell
                  center
                  width={TOGGLE_COLUMN_WIDTH}
                  style={styles.bodyCell}
                >
                  <Switch
                    checked={conversionUnit.active}
                    disabled={!onActiveChange}
                    onChange={(_, active) =>
                      onActiveChange?.(conversionUnit.id, active)
                    }
                    sx={{ margin: 0 }}
                    data-cy={`${dataCy}ActiveSwitch-${conversionUnit.unit}`}
                  />
                </TableCell>
                {showAddedColumns && (
                  <TableCell style={styles.bodyCell}>
                    {getUserFullName(conversionUnit.createdBy)}
                  </TableCell>
                )}
                {showAddedColumns && (
                  <TableCell style={styles.bodyCell}>
                    {setDisplayDate(conversionUnit.createdDate)}
                  </TableCell>
                )}
                {showActions && (
                  <TableCell
                    width={ACTIONS_COLUMN_WIDTH}
                    style={styles.bodyCell}
                  >
                    <div className={classes.actions}>
                      {onEdit && canModify(conversionUnit) && (
                        <IconButton
                          style={styles.iconButton}
                          onClick={() => onEdit(conversionUnit)}
                          data-cy={`${dataCy}EditButton-${conversionUnit.unit}`}
                        >
                          <Icon icon={EditIcon} />
                        </IconButton>
                      )}
                      {onDelete && canModify(conversionUnit) && (
                        <IconButton
                          style={styles.iconButton}
                          onClick={() => onDelete(conversionUnit.id)}
                          data-cy={`${dataCy}DeleteButton-${conversionUnit.unit}`}
                        >
                          <Icon icon={DeleteIcon} />
                        </IconButton>
                      )}
                    </div>
                  </TableCell>
                )}
              </TableRow>
            ))
          )}
        </TableBody>
      </ListTable>
    </div>
  );
};

export default ConversionUnitsTable;
